/*
 * ARCHER — Morphing Dot Cursor
 * A precise inner dot + a trailing ring that morphs to wrap interactive elements.
 * Zero dependencies (rAF + lerp), self-injecting (CSS + DOM), self-hosted.
 *
 * Design intent: adaptive-contrast — the cursor samples the background luminance
 * under the pointer and renders dark-on-light / light-on-dark (plus a soft
 * contrasting halo) so it stays legible across the dark hero, white content
 * sections, mid-tones and imagery alike. (Earlier mix-blend-mode: difference
 * vanished over mid-grey / photographic mid-tones — hence the switch.) Disabled
 * on touch / coarse pointers and when the visitor prefers reduced motion.
 *
 * Behaviour tiers, auto-detected via event delegation:
 *   MORPH — button-like controls: the ring detaches from the mouse and morphs
 *           (position + size + radius) to wrap the element, dot keeps tracking.
 *   GROW  — plain links / inputs: the ring scales up, the dot shrinks.
 */
(function () {
  'use strict';

  // Never inside an iframe (would double-render on embedded previews).
  if (window.top !== window.self) return;

  var CFG = {
    dot: 7,            // inner dot diameter (px)
    ring: 34,          // resting ring diameter (px)
    growScale: 1.55,   // ring multiplier over plain links / inputs
    morphPad: 5,       // px of halo added around a wrapped control
    ringLerp: 0.18,    // ring follow easing (lower = more lag / spring)
    dotLerp: 0.42,     // dot follow easing (higher = tighter to the mouse)
    press: 0.86        // scale applied on mousedown (tactile feedback)
  };

  // [data-cursor="plain"] opts an interactive element out of the box-morph (keeps the plain dot/ring).
  var MORPH_SEL = 'button:not([data-cursor="plain"]), .w-button, [role="button"], input[type="submit"], input[type="button"], [data-cursor="morph"]';
  var GROW_SEL  = 'a, summary, label, select, textarea, input, .w-nav-link, [data-cursor="grow"]';
  // Zones that run their own cursor (e.g. the PDP gallery arrow chip): suppress
  // ours entirely so the dot/ring don't double up on top of theirs.
  var HIDE_SEL  = '[data-cursor="none"]';

  // Bail on coarse pointers / reduced-motion — keep the native cursor there.
  var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!fine || still) return;

  function init() {
    var root = document.documentElement;

    var style = document.createElement('style');
    style.textContent =
      'html.archer-cursor-on, html.archer-cursor-on * { cursor: none !important; }' +
      '#archer-cursor-dot, #archer-cursor-ring {' +
        'position: fixed; top: 0; left: 0; pointer-events: none; z-index: 2147483000;' +
        'will-change: transform, width, height;' +
        'opacity: 0; --c: #fff; --halo: rgba(0,0,0,.38);' +          // light cursor / dark halo = default (dark bg)
        'transition: opacity .25s ease;' +
      '}' +
      // Dark cursor / light halo on light backgrounds — toggled by the luminance sampler.
      'html.archer-cursor-onlight #archer-cursor-dot, html.archer-cursor-onlight #archer-cursor-ring {' +
        '--c: #1b1b1b; --halo: rgba(255,255,255,.7);' +
      '}' +
      'html.archer-cursor-ready #archer-cursor-dot, html.archer-cursor-ready #archer-cursor-ring { opacity: 1; }' +
      'html.archer-cursor-hidden #archer-cursor-dot, html.archer-cursor-hidden #archer-cursor-ring { opacity: 0 !important; }' +
      // Suppressed over a self-cursor zone (gallery stage). Separate from -hidden,
      // which mousemove clears every frame; -off persists until the pointer leaves.
      'html.archer-cursor-off #archer-cursor-dot, html.archer-cursor-off #archer-cursor-ring { opacity: 0 !important; }' +
      '#archer-cursor-dot {' +
        'width: ' + CFG.dot + 'px; height: ' + CFG.dot + 'px; margin: ' + (-CFG.dot / 2) + 'px 0 0 ' + (-CFG.dot / 2) + 'px;' +
        'background: var(--c); border-radius: 50%; box-shadow: 0 0 0 1px var(--halo), 0 0 4px var(--halo);' +
        'transition: opacity .2s ease, background-color .18s ease, box-shadow .18s ease;' +
      '}' +
      '#archer-cursor-ring {' +
        'border: 1.5px solid var(--c); border-radius: 50%; box-sizing: border-box;' +
        'box-shadow: 0 0 0 1px var(--halo), 0 0 4px var(--halo);' +
        'transition: border-color .18s ease, box-shadow .18s ease, opacity .25s ease;' +
      '}' +
      'html.archer-cursor-morph #archer-cursor-dot { opacity: .55; }';
    document.head.appendChild(style);

    var dot = document.createElement('div');
    dot.id = 'archer-cursor-dot';
    dot.setAttribute('aria-hidden', 'true');
    var ring = document.createElement('div');
    ring.id = 'archer-cursor-ring';
    ring.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);
    document.body.appendChild(ring);
    root.classList.add('archer-cursor-on', 'archer-cursor-onlight'); // default: dark cursor (site is mostly light)

    var mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    var d = { x: mouse.x, y: mouse.y };                                    // dot current
    var r = { x: mouse.x, y: mouse.y, w: CFG.ring, h: CFG.ring, rad: CFG.ring / 2 }; // ring current
    var press = 1, dotScale = 1, started = false, morphEl = null;

    function lerp(a, b, t) { return a + (b - a) * t; }

    // Adaptive contrast: pick the cursor colour from the first opaque background
    // under the pointer. Unknown (image / all-transparent) is treated as light,
    // matching the site's predominantly white surfaces; the halo covers the rest.
    var lastSX = -99, lastSY = -99;
    function sampleContrast(x, y) {
      if (Math.abs(x - lastSX) < 4 && Math.abs(y - lastSY) < 4) return; // cheap throttle
      lastSX = x; lastSY = y;
      var el = document.elementFromPoint(x, y), hops = 0, L = null;
      while (el && hops < 8) {
        var tag = el.tagName;
        // Replaced image/video layer: the bg-colour beneath isn't what's on screen.
        if (tag === 'IMG' || tag === 'VIDEO' || tag === 'CANVAS' || tag === 'PICTURE' || tag === 'svg') break;
        var cs = getComputedStyle(el);
        // An opaque background-colour wins even if a pattern/gradient sits over it
        // (e.g. the dotted grid on the black spec sections stays "black").
        var m = cs.backgroundColor.match(/rgba?\(([^)]+)\)/);
        if (m) {
          var p = m[1].split(',');
          var a = p[3] === undefined ? 1 : parseFloat(p[3]);
          if (a > 0.2) { L = (0.2126 * +p[0] + 0.7152 * +p[1] + 0.0722 * +p[2]) / 255; break; }
        }
        // Transparent element carrying a raster photo (url()) → unknown (light default + halo).
        // Gradients / patterns (e.g. the dotted grid) fall through to the base colour below.
        if (cs.backgroundImage.indexOf('url(') !== -1) break;
        el = el.parentElement; hops++;
      }
      root.classList.toggle('archer-cursor-onlight', L === null ? true : L > 0.5);
    }

    document.addEventListener('mousemove', function (e) {
      mouse.x = e.clientX; mouse.y = e.clientY;
      sampleContrast(mouse.x, mouse.y);
      if (!started) {
        started = true;
        d.x = r.x = mouse.x; d.y = r.y = mouse.y;
        root.classList.add('archer-cursor-ready');
      }
      root.classList.remove('archer-cursor-hidden');
    }, { passive: true });

    // Leave / enter window, tab blur — fade the cursor out cleanly.
    document.addEventListener('mouseleave', function () { root.classList.add('archer-cursor-hidden'); });
    window.addEventListener('blur', function () { root.classList.add('archer-cursor-hidden'); });
    document.addEventListener('mousedown', function () { press = CFG.press; });
    document.addEventListener('mouseup', function () { press = 1; });

    // Delegation: works for dynamically inserted nodes (popups, splash trail, etc.).
    document.addEventListener('mouseover', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1) return;
      if (t.closest && t.closest(HIDE_SEL)) { root.classList.add('archer-cursor-off'); return; }
      var m = t.closest && t.closest(MORPH_SEL);
      if (m) { morphEl = m; dotScale = 1; root.classList.add('archer-cursor-morph'); root.classList.remove('archer-cursor-grow'); return; }
      var g = t.closest && t.closest(GROW_SEL);
      if (g) { morphEl = null; dotScale = 0.4; root.classList.add('archer-cursor-grow'); root.classList.remove('archer-cursor-morph'); }
    });
    document.addEventListener('mouseout', function (e) {
      var to = e.relatedTarget;
      // Restore our cursor once the pointer leaves the self-cursor zone.
      if (!(to && to.closest && to.closest(HIDE_SEL))) root.classList.remove('archer-cursor-off');
      // Only reset when leaving to something that isn't itself interactive.
      if (to && to.closest && (to.closest(MORPH_SEL) || to.closest(GROW_SEL))) return;
      morphEl = null; dotScale = 1;
      root.classList.remove('archer-cursor-morph', 'archer-cursor-grow');
    });

    function frame() {
      // Target ring geometry: wrap the morph element, else a circle at the mouse.
      var tx, ty, tw, th, trad;
      if (morphEl && document.body.contains(morphEl)) {
        var b = morphEl.getBoundingClientRect();
        tw = b.width + CFG.morphPad * 2;
        th = b.height + CFG.morphPad * 2;
        tx = b.left + b.width / 2;
        ty = b.top + b.height / 2;
        var cr = parseFloat(getComputedStyle(morphEl).borderTopLeftRadius) || 0;
        trad = Math.min(cr + CFG.morphPad, Math.min(tw, th) / 2);
      } else {
        var s = root.classList.contains('archer-cursor-grow') ? CFG.growScale : 1;
        tw = th = CFG.ring * s;
        trad = tw / 2;
        tx = mouse.x; ty = mouse.y;
      }

      r.x = lerp(r.x, tx, CFG.ringLerp);
      r.y = lerp(r.y, ty, CFG.ringLerp);
      r.w = lerp(r.w, tw, CFG.ringLerp);
      r.h = lerp(r.h, th, CFG.ringLerp);
      r.rad = lerp(r.rad, trad, CFG.ringLerp);
      d.x = lerp(d.x, mouse.x, CFG.dotLerp);
      d.y = lerp(d.y, mouse.y, CFG.dotLerp);

      ring.style.width = r.w + 'px';
      ring.style.height = r.h + 'px';
      ring.style.borderRadius = r.rad + 'px';
      ring.style.transform = 'translate3d(' + (r.x - r.w / 2) + 'px,' + (r.y - r.h / 2) + 'px,0) scale(' + press + ')';
      dot.style.transform = 'translate3d(' + d.x + 'px,' + d.y + 'px,0) scale(' + (dotScale * press) + ')';

      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  if (document.body) init();
  else document.addEventListener('DOMContentLoaded', init);
})();
